import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { createLotusCareStateApi } from '@/shared/api/lotus';
import {
  ANALYTICS_EVENTS,
  analytics,
  endWorkspaceSession,
  startWorkspaceSession,
} from '@/shared/lib';

import {
  createCareStateClient,
  type CareStateStatus,
  type CareStateUpdate,
  useCareState,
} from '@/entities/care-state-engine';

import type { CareStateDomainDb, ClinicalScopeT } from '../model/types';

export type CareStateDomainContextValue = {
  patientUserId: string;
  careState: CareStateDomainDb | null;
  scope: ClinicalScopeT;
  setScope(next: ClinicalScopeT): void;
  status: CareStateStatus;
  lastError: string | null;
  debug: {
    serverCursor: number | null;
    pendingUpdateCount: number;
    pendingUnsubmittedCount: number;
    pendingSubmittedCount: number;
  };
  update(update: CareStateUpdate<CareStateDomainDb>): void;
};

const CareStateDomainContext = createContext<CareStateDomainContextValue | null>(null);

export type CareStateProviderProps = {
  patientUserId: string;
  children: React.ReactNode;
};

/**
 * Owns the CareState polling lifecycle for a patient workspace and the
 * workspace's coarse `scope` hint. Scope is type-only now — list-style tabs
 * render every problem / request, so there is no per-entity selection
 * derivation here.
 */
export function CareStateProvider({ patientUserId, children }: CareStateProviderProps) {
  const api = useMemo(() => createLotusCareStateApi(), []);
  const client = useMemo(
    () =>
      createCareStateClient<CareStateDomainDb>({
        userId: patientUserId,
        api,
      }),
    [api, patientUserId]
  );

  const {
    state: careState,
    update,
    status,
    lastError,
    debug,
  } = useCareState<CareStateDomainDb>(client);

  const [scope, setScope] = useState<ClinicalScopeT>({ type: 'patient' });
  const previousScopeTypeRef = useRef<ClinicalScopeT['type']>('patient');

  useEffect(() => {
    if (patientUserId.trim() === '') {
      return;
    }

    startWorkspaceSession(patientUserId);
    return () => {
      endWorkspaceSession(patientUserId);
    };
  }, [patientUserId]);

  useEffect(() => {
    const previousType = previousScopeTypeRef.current;
    if (previousType === scope.type) {
      return;
    }

    previousScopeTypeRef.current = scope.type;
    void analytics.track({
      name: ANALYTICS_EVENTS.carestate.scopeChanged,
      context: {
        actor: 'clinician',
        patientUserId,
        scope,
      },
      properties: {
        fromScopeType: previousType,
        toScopeType: scope.type,
      },
    });
  }, [patientUserId, scope]);

  const value: CareStateDomainContextValue = useMemo(
    () => ({
      patientUserId,
      careState,
      scope,
      setScope,
      status,
      lastError,
      debug,
      update,
    }),
    [patientUserId, careState, scope, status, lastError, debug, update]
  );

  return (
    <CareStateDomainContext.Provider value={value}>{children}</CareStateDomainContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCareStateDomain(): CareStateDomainContextValue {
  const ctx = useContext(CareStateDomainContext);
  if (ctx === null) {
    throw new Error('useCareStateDomain must be used within a CareStateProvider');
  }
  return ctx;
}
